import { resolvedApiBaseUrl } from '../lib/apiBase';
import { firebaseAuthHeaders } from '../lib/authHeaders';
import { getFirebaseAuth } from '../lib/firebase';
import { readOpsIdToken, readOpsUid } from '../lib/idTokenStore';
import { needsTermsAcceptance, type UserProfile } from '../users/api';
import { syncProfileWithJoinGate } from './joinGate';

export class TermsRequiredError extends Error {
  constructor(readonly profile: UserProfile) {
    super('terms_required');
    this.name = 'TermsRequiredError';
  }
}

export class SessionUidMismatchError extends Error {
  constructor(
    readonly firebaseUid: string,
    readonly profileId: string,
  ) {
    super(`session_uid_mismatch firebase=${firebaseUid} profile=${profileId}`);
    this.name = 'SessionUidMismatchError';
  }
}

/** Soft-clear Hosting __session without revoking Firebase refresh tokens. */
export async function softClearSessionCookie(): Promise<void> {
  try {
    await fetch(`${resolvedApiBaseUrl()}/auth/session?soft=1`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'X-Session-Clear': 'soft' },
    });
  } catch {
    /* ignore network */
  }
}

async function exchangeSessionCookie(idToken: string): Promise<Response> {
  return fetch(`${resolvedApiBaseUrl()}/auth/session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(await firebaseAuthHeaders()),
    },
    body: JSON.stringify({ idToken }),
  });
}

/**
 * Exchange Firebase ID token for httpOnly session cookie, then sync Firestore profile.
 * Hardened for invite / account-switch: soft-clear prior cookie, force-refresh token, retry, uid check.
 */
export async function establishSession(): Promise<UserProfile> {
  const stored = readOpsIdToken();
  const storedUid = readOpsUid();
  let firebaseUid = storedUid || '';
  let idToken = stored || '';

  if (!idToken) {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not signed in');
    }
    firebaseUid = user.uid;
    await softClearSessionCookie();
    idToken = await user.getIdToken(true);
  } else {
    await softClearSessionCookie();
  }
  let sessionOk = false;
  try {
    let res = await exchangeSessionCookie(idToken);
    if (res.status === 401 && !stored) {
      // Token may still be minting after email-link sign-in.
      await new Promise((r) => setTimeout(r, 400));
      const user = getFirebaseAuth().currentUser;
      if (user) {
        idToken = await user.getIdToken(true);
        res = await exchangeSessionCookie(idToken);
      }
    }
    sessionOk = res.ok || res.status === 204;
    if (!sessionOk) {
      console.warn('establishSession: session cookie exchange failed', res.status);
    }
  } catch {
    // Bearer auth via apiFetch still works when cookie exchange is unavailable.
  }

  const profile = await syncProfileWithJoinGate();
  if (profile.id && profile.id !== firebaseUid) {
    // Stale cookie / wrong principal — clear and retry once under Bearer-only.
    console.warn('establishSession: profile uid mismatch, retrying', profile.id, firebaseUid);
    await softClearSessionCookie();
    const user = getFirebaseAuth().currentUser;
    if (user) {
      idToken = await user.getIdToken(true);
    }
    try {
      await exchangeSessionCookie(idToken);
    } catch {
      /* ignore */
    }
    const retry = await syncProfileWithJoinGate();
    if (retry.id && retry.id !== firebaseUid) {
      throw new SessionUidMismatchError(firebaseUid, retry.id);
    }
    if (needsTermsAcceptance(retry)) {
      throw new TermsRequiredError(retry);
    }
    return retry;
  }

  if (!sessionOk) {
    // Cookie failed but profile matches Firebase user — usable via Bearer.
    console.warn('establishSession: continuing with Bearer auth (no session cookie)');
  }

  if (needsTermsAcceptance(profile)) {
    throw new TermsRequiredError(profile);
  }
  return profile;
}
